// Mutation: crea un plan + sus plan_ejercicios atómicamente vía RPC
// `prescribir_rutina_a_paciente` (migration 0017). Usado por
// PrescribirRutina al click "Guardar y prescribir".
//
// El caller pasa `pacienteId` — la RPC resuelve internamente el contenedor
// invisible (find-or-create) + delega a `crear_plan_con_ejercicios` (0004).
// Ver docs/decisions/deuda-tecnica-conocida.md sección "Deprecar tratamientos
// como entidad separada" (Approach A).
//
// Patrón coherente con useCrearSesion: el caller hace `await crear(input)`,
// si recibe id navega; si recibe null muestra el error que quedó en state.

import { useState } from "react";
import { supabase } from "../client";

export interface CrearPlanEjercicioInput {
  ejercicio_id: string;
  orden: number;
  reps_objetivo: number;
  series_objetivo: number;
  /** Segundos de descanso entre series. null si no se especifica. */
  descanso_seg: number | null;
  /** Frecuencia semanal del ejercicio dentro del plan. Sprint 2 va a
   *  popular esto consistente para usar en cálculo de adherencia. */
  frecuencia_semanal: number | null;
  notas_kine: string | null;
}

export interface CrearPlanInput {
  pacienteId: string;
  /** UUID de la rutina template origen, NULL si el plan se construye
   *  directo sin template (Camino C del Sprint 1). */
  rutinaOrigenId: string | null;
  nombre: string;
  /** ISO date "YYYY-MM-DD". Default sugerido: hoy. */
  fechaInicio: string;
  notasKine: string | null;
  ejercicios: CrearPlanEjercicioInput[];
}

export interface UseCrearPlanReturn {
  crear: (input: CrearPlanInput) => Promise<string | null>;
  loading: boolean;
  error: Error | null;
}

export function useCrearPlan(): UseCrearPlanReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function crear(input: CrearPlanInput): Promise<string | null> {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc(
        "prescribir_rutina_a_paciente",
        {
          p_paciente_id: input.pacienteId,
          p_rutina_origen_id: input.rutinaOrigenId,
          p_nombre: input.nombre,
          p_fecha_inicio: input.fechaInicio,
          p_notas_kine: input.notasKine,
          p_ejercicios: input.ejercicios.map((e) => ({
            ejercicio_id: e.ejercicio_id,
            orden: e.orden,
            reps_objetivo: e.reps_objetivo,
            series_objetivo: e.series_objetivo,
            descanso_seg: e.descanso_seg,
            frecuencia_semanal: e.frecuencia_semanal,
            notas_kine: e.notas_kine,
          })),
        },
      );

      if (rpcError) throw rpcError;
      if (typeof data !== "string") {
        throw new Error("RPC no devolvió plan_id");
      }
      return data;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      console.error("[useCrearPlan] failed:", err);
      return null;
    } finally {
      setLoading(false);
    }
  }

  return { crear, loading, error };
}
